import React, { useState, useEffect } from 'react'
import { apiFetch } from '../services/auth'

interface TimeRegistration {
  id?: string
  costCenter?: any
  customer?: {
    id?: string
    name?: string
    number?: string
    active?: any
    deleted?: any
    priceList?: any
    accountManager?: any
    defaultProjectId?: any
    isBusiness?: any
    propertyDesignation?: any
    invoiceCity?: any
    invoiceZipCode?: any
  }
  project?: {
    id?: string
    description?: string
    projectLeader?: any
    startDate?: any
    endDate?: any
    status?: number
    orderTime?: any
  }
  service?: {
    id?: string
    description?: string
    active?: any
    purchasePrice?: any
    salesPrices?: any
    type?: any
    unit?: any
    isStock?: any
  }
  registrationCode?: {
    code?: string
    name?: string
    priceMultiplier?: number
    costMultiplier?: number
    type?: string
    active?: any
    id?: string
  }
  workedDate?: string
  workedHours?: number
  chargeHours?: number
  startTime?: any
  stopTime?: any
  invoiceText?: any
  note?: any
  userId?: string
  createdBy?: any
  updatedBy?: any
  invoiceBasisId?: any
  nonInvoiceable?: boolean
  childId?: any
  createdTime?: any
  unitCost?: number
  unitPrice?: number
  documentId?: any
  documentType?: any
  
  // Legacy fields for backward compatibility
  employeeId?: string
  articleId?: string
  date?: string
  duration?: number
  description?: string
  billable?: boolean
  invoiced?: boolean
  approved?: boolean
  createdAt?: string
  updatedAt?: string
}



interface ProjectBreakdown {
  projectId: string
  projectName: string
  hours: number
}

interface MonthlyReport {
  month: string
  customer: string
  totalHours: number
  registrations: TimeRegistration[]
  projects: ProjectBreakdown[]
}

export function TimeReports() {
  const [timeRegistrations, setTimeRegistrations] = useState<TimeRegistration[]>([])
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set())

  const buildDateRange = (year: number, monthIndex: number | null) => {
    const startMonth = monthIndex === null ? 0 : monthIndex
    const endMonth = monthIndex === null ? 11 : monthIndex

    const fromDate = new Date(Date.UTC(year, startMonth, 1)).toISOString().slice(0, 10)
    const toDate = new Date(Date.UTC(year, endMonth + 1, 0)).toISOString().slice(0, 10)

    return { fromDate, toDate }
  }

  const fetchTimeReports = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const { fromDate, toDate } = buildDateRange(selectedYear, selectedMonth)
      const query = new URLSearchParams({ fromDate, toDate })

      // // console.log('Fetching time reports from:', `${apiUrl}/fortnox/time-reports?${query.toString()}`)
      const timeReportsResponse = await apiFetch(`/fortnox/time-reports?${query.toString()}`)

      if (!timeReportsResponse.ok) {
        const errorText = await timeReportsResponse.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        throw new Error(errorMessage || `Failed to fetch time reports: ${timeReportsResponse.status} ${timeReportsResponse.statusText}`)
      }

      const responseText = await timeReportsResponse.text()
      // // console.log('Time reports response:', responseText)
      
      let timeReportsData
      try {
        timeReportsData = JSON.parse(responseText)
      } catch (parseError) {
        // // console.error('JSON parse error:', parseError)
        // // console.error('Response text:', responseText)
        throw new Error('Invalid JSON response from server')
      }
      
      // // console.log('Parsed time reports data:', timeReportsData)
      // // console.log('Data type:', typeof timeReportsData)
      // // console.log('Data keys:', Object.keys(timeReportsData || {}))
      // // console.log('Is array:', Array.isArray(timeReportsData))
      
      // Handle different possible response structures
      let registrations = []
      if (Array.isArray(timeReportsData)) {
        registrations = timeReportsData
      } else if (timeReportsData.TimeRegistrations) {
        registrations = timeReportsData.TimeRegistrations
      } else if (timeReportsData.timeRegistrations) {
        registrations = timeReportsData.timeRegistrations
      } else if (timeReportsData.data) {
        registrations = Array.isArray(timeReportsData.data) ? timeReportsData.data : timeReportsData.data.TimeRegistrations || []
      } else {
        // // console.warn('Unknown response structure:', timeReportsData)
        registrations = []
      }
      
      // // console.log('Final registrations array:', registrations)
      // // console.log('Registrations count:', registrations.length)
      
      setTimeRegistrations(registrations)
      
    } catch (err) {
      // // console.error('Fetch error:', err)
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const aggregateReportsByMonth = () => {
    const aggregated: { [key: string]: MonthlyReport } = {}
    
    // // console.log('Aggregating reports from registrations:', timeRegistrations)
    
    timeRegistrations.forEach((registration: TimeRegistration) => {
      // Handle both old and new data structures
      const workDate = registration.workedDate || registration.date
      const customerName = registration.customer?.name || (typeof registration.customer === 'string' ? registration.customer : '')
      const hours = registration.workedHours || registration.chargeHours || registration.duration || 0
      
      // // console.log('Processing registration:', { workDate, customerName, hours, registration })
      
      if (!workDate || !customerName) {
        // // console.log('Skipping registration - missing date or customer:', { workDate, customerName })
        return
      }
      
      const date = new Date(workDate)
      const year = date.getFullYear()
      const month = date.getMonth()
      
      // // console.log('Date parsing:', { workDate, year, month, selectedYear, selectedMonth })
      
      // Filter by selected year and month if specified
      if (year !== selectedYear) {
        // // console.log('Skipping - wrong year:', year, 'vs', selectedYear)
        return
      }
      if (selectedMonth !== null && month !== selectedMonth) {
        // // console.log('Skipping - wrong month:', month, 'vs', selectedMonth)
        return
      }
      
      // If no specific month is selected (Alla månader), aggregate by customer for the whole year
      // Otherwise, aggregate by month and customer
      const reportKey = selectedMonth === null 
        ? `${year}-${customerName}` 
        : `${year}-${month.toString().padStart(2, '0')}-${customerName}`
      
      if (!aggregated[reportKey]) {
        aggregated[reportKey] = {
          month: selectedMonth === null 
            ? `${year}` 
            : new Date(year, month).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' }),
          customer: customerName,
          totalHours: 0,
          registrations: [],
          projects: []
        }
      }
      
      aggregated[reportKey].totalHours += hours
      aggregated[reportKey].registrations.push(registration)
      
      // Track project hours
      const projectId = registration.project?.id || 'unknown'
      const projectName = registration.project?.description || 'Okänt projekt'
      
      let existingProject = aggregated[reportKey].projects.find(p => p.projectId === projectId)
      if (!existingProject) {
        existingProject = {
          projectId,
          projectName,
          hours: 0
        }
        aggregated[reportKey].projects.push(existingProject)
      }
      existingProject.hours += hours
      
      // // console.log('Added to aggregated:', reportKey, aggregated[reportKey])
    })
    
    const result = Object.values(aggregated).sort((a, b) => {
      // Sort by customer name when showing yearly totals, otherwise by month then customer
      if (selectedMonth === null) {
        return a.customer.localeCompare(b.customer)
      } else {
        const monthCompare = a.month.localeCompare(b.month)
        if (monthCompare !== 0) return monthCompare
        return a.customer.localeCompare(b.customer)
      }
    })
    
    // // console.log('Final aggregated result:', result)
    return result
  }

  useEffect(() => {
    fetchTimeReports()
  }, [])

  useEffect(() => {
    setMonthlyReports(aggregateReportsByMonth())
  }, [timeRegistrations, selectedYear, selectedMonth])

  const months = [
    { value: null, label: 'Alla månader' },
    { value: 0, label: 'Januari' },
    { value: 1, label: 'Februari' },
    { value: 2, label: 'Mars' },
    { value: 3, label: 'April' },
    { value: 4, label: 'Maj' },
    { value: 5, label: 'Juni' },
    { value: 6, label: 'Juli' },
    { value: 7, label: 'Augusti' },
    { value: 8, label: 'September' },
    { value: 9, label: 'Oktober' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i)

  const toggleCustomerExpansion = (customerKey: string) => {
    const newExpanded = new Set(expandedCustomers)
    if (newExpanded.has(customerKey)) {
      newExpanded.delete(customerKey)
    } else {
      newExpanded.add(customerKey)
    }
    setExpandedCustomers(newExpanded)
  }

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        textAlign: 'center'
      }}>
        <p>Laddar tidrapporter...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#dc2626', margin: '0 0 8px 0' }}>Fel</h3>
          <p style={{ color: '#dc2626', margin: '0' }}>{error}</p>
        </div>
        <button
          onClick={fetchTimeReports}
          style={{
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          Försök igen
        </button>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '32px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ color: '#1a202c', marginBottom: '16px', fontSize: '24px', fontWeight: '600' }}>
          Tidrapporter per kund och månad
        </h2>
        
        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              År
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Månad
            </label>
            <select
              value={selectedMonth ?? ''}
              onChange={(e) => setSelectedMonth(e.target.value === '' ? null : parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {months.map(month => (
                <option key={month.value ?? 'all'} value={month.value ?? ''}>{month.label}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              onClick={fetchTimeReports}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Uppdatera
            </button>
          </div>
        </div>
      </div>

      {monthlyReports.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <p>Inga tidrapporter hittades för den valda perioden.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151', width: '150px' }}>
                  {selectedMonth === null ? 'År' : 'Månad'}
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151', width: 'auto' }}>
                  Kund
                </th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151', width: '120px' }}>
                  Totala timmar
                </th>

              </tr>
            </thead>
            <tbody>
              {monthlyReports.map((report: MonthlyReport, index: number) => {
                const customerKey = `${report.month}-${report.customer}`
                const isExpanded = expandedCustomers.has(customerKey)
                
                return (
                  <React.Fragment key={customerKey}>
                    <tr style={{ 
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleCustomerExpansion(customerKey)}
                    >
                      <td style={{ padding: '12px', color: '#374151' }}>
                        {report.month}
                      </td>
                      <td style={{ padding: '12px', color: '#374151' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            width: '16px',
                            textAlign: 'center',
                            userSelect: 'none'
                          }}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                          <span style={{ fontWeight: '500' }}>{report.customer}</span>
                          {report.projects.length > 1 && (
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              ({report.projects.length} projekt)
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#374151', fontWeight: '500' }}>
                        {report.totalHours.toFixed(1)} h
                      </td>
                    </tr>
                    
                    {isExpanded && report.projects.map((project: ProjectBreakdown, projectIndex: number) => (
                      <tr key={`${customerKey}-${project.projectId}`} style={{
                        backgroundColor: '#f9fafb',
                        borderBottom: projectIndex === report.projects.length - 1 ? '2px solid #e2e8f0' : '1px solid #f3f4f6'
                      }}>
                        <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '14px' }}>
                          {/* Empty for month column */}
                        </td>
                        <td style={{ padding: '8px 12px 8px 40px', color: '#374151', fontSize: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#9ca3af' }}>└</span>
                            <span>{project.projectName}</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280', fontSize: '14px' }}>
                          {project.hours.toFixed(1)} h
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
          
          {/* Summary */}
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', color: '#374151' }}>Totalt:</span>
              <span style={{ fontWeight: '600', color: '#374151' }}>
                {monthlyReports.reduce((sum: number, report: MonthlyReport) => sum + report.totalHours, 0).toFixed(1)} timmar
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
